import {
  adaptSegmentPreview,
  type CreateCreatorSegmentDto,
  handleSegmentFilter,
  handleSegmentPreviewParams,
  type PreviewCreatorSegmentDto,
  type Segment,
  type SegmentDetail,
  type SegmentPreviewFilter,
  SEGMENTS_PAGE_LIMIT,
  type SegmentSelectorFilter,
  type SegmentsFilter,
  type UpdateCreatorSegmentDto,
  type UseGetSegmentPreviewReturn,
} from '@metapic-apps/shared'
import { computed, type MaybeRefOrGetter, type Ref, ref, toValue, watch } from 'vue'

import { useApi } from '@/lib/helpers/api.helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { socialMediaClient } from '@/lib/services/social-media.service'
import { useErrorStore } from '@/stores/error'
import { useUserStore } from '@/stores/user'

const CREATOR_SEGMENTS_LIMIT = 100

type UseGetSegmentsReturn = {
  data: Ref<Segment[]>
  totalPages: Ref<number>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  refetch: () => Promise<void>
}

export const useGetSegments = (filterRef: Ref<SegmentsFilter>): UseGetSegmentsReturn => {
  const data = ref<Segment[]>([])
  const totalPages = ref<number>(0)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  const userStore = useUserStore()

  const fetchSegments = async (filter: SegmentsFilter) => {
    try {
      data.value = []
      isLoading.value = true
      error.value = null

      const currentUserId = userStore.id != null ? String(userStore.id) : undefined
      const result = await socialMediaClient.creatorSegments.listSegments(
        handleSegmentFilter(filter, { currentUserId }),
      )
      data.value = result.items
      totalPages.value = result.meta.totalPages
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  const refetch = () => fetchSegments(filterRef.value)

  watch(
    filterRef,
    async () => {
      await fetchSegments(filterRef.value)
    },
    { immediate: true, deep: true },
  )

  return {
    data,
    totalPages,
    isLoading,
    error,
    refetch,
  }
}

type UseGetCreatorSegmentsReturn = {
  staticSegments: Ref<Segment[]>
  dynamicSegments: Ref<Segment[]>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  refetch: () => Promise<void>
}

export const useGetCreatorSegments = (
  creatorId: Ref<number> | number,
  options: { enabled?: Ref<boolean> | boolean } = {},
): UseGetCreatorSegmentsReturn => {
  const { enabled = true } = options
  const staticSegments = ref<Segment[]>([])
  const dynamicSegments = ref<Segment[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchCreatorSegments = async () => {
    if (!toValue(enabled)) {
      return
    }
    try {
      isLoading.value = true
      error.value = null

      const result = await socialMediaClient.creatorSegments.listSegments({
        member_user_id: String(toValue(creatorId)),
        limit: CREATOR_SEGMENTS_LIMIT,
      })
      staticSegments.value = result.items.filter((segment) => segment.type === 'static')
      dynamicSegments.value = result.items.filter((segment) => segment.type === 'dynamic')
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  const refetch = () => fetchCreatorSegments()

  watch(() => [toValue(creatorId), toValue(enabled)], fetchCreatorSegments, { immediate: true })

  return {
    staticSegments,
    dynamicSegments,
    isLoading,
    error,
    refetch,
  }
}

export const useSearchStaticSegments = (queryRef: Ref<string>) => {
  const data = ref<Segment[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const search = async (query: string) => {
    try {
      isLoading.value = true
      error.value = null

      const result = await socialMediaClient.creatorSegments.listSegments({
        type: 'static',
        name: query || undefined,
        limit: SEGMENTS_PAGE_LIMIT,
      })
      data.value = result.items
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(queryRef, (query) => search(query), { immediate: true })

  return {
    data,
    isLoading,
    error,
  }
}

export const useSearchAdvertiserGroupSegments = (
  filter: () => { advertiserGroupId: number; query: string },
  { skip = false }: { skip?: MaybeRefOrGetter<boolean> } = {},
) => {
  const { data, isLoading, error } = useApi(async (signal) => {
    const { advertiserGroupId, query } = filter()
    if (toValue(skip)) {
      return []
    }

    const result = await socialMediaClient.creatorSegments.listSegments(
      {
        advertiser_group_id: advertiserGroupId,
        name: query.trim() || undefined,
        limit: SEGMENTS_PAGE_LIMIT,
      },
      { signal },
    )
    return result.items
  })

  return {
    data: computed(() => data.value ?? []),
    isLoading,
    error,
  }
}

export const useSearchSegments = (filterRef: Ref<SegmentSelectorFilter>) => {
  const { data, isLoading, error } = useApi(async (signal) => {
    const { advertiserGroup, query } = filterRef.value

    const result = await socialMediaClient.creatorSegments.listSegments(
      {
        advertiser_group_id: advertiserGroup?.id,
        name: query?.trim() || undefined,
        limit: SEGMENTS_PAGE_LIMIT,
      },
      { signal },
    )
    return result.items
  })

  return {
    data: computed(() => data.value ?? []),
    isLoading,
    error,
  }
}

export const useAddCreatorToSegment = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const addToSegment = async (segmentId: string, creatorId: number): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null

      await socialMediaClient.creatorSegments.addMembers(segmentId, {
        user_ids: [String(creatorId)],
      })
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return {
    addToSegment,
    isLoading,
    error,
  }
}

export const useRemoveCreatorFromSegment = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const removeFromSegment = async (segmentId: string, creatorId: number): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null

      await socialMediaClient.creatorSegments.removeMember(segmentId, String(creatorId))
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return {
    removeFromSegment,
    isLoading,
    error,
  }
}

type UseGetSegmentReturn = {
  data: Ref<SegmentDetail | null>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  refetch: () => Promise<void>
}

export const useGetSegment = (id: Ref<string>): UseGetSegmentReturn => {
  const data = ref<SegmentDetail | null>(null)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchSegment = async (segmentId: string) => {
    try {
      isLoading.value = true
      error.value = null
      data.value = null
      data.value = await socialMediaClient.creatorSegments.findOne(segmentId)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  const refetch = () => fetchSegment(id.value)

  watch(id, async () => await fetchSegment(id.value), { immediate: true })

  return { data, isLoading, error, refetch }
}

type UseSaveSegmentReturn<T> = {
  save: (payload: T) => Promise<SegmentDetail | null>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
}

export const useCreateSegment = (): UseSaveSegmentReturn<CreateCreatorSegmentDto> => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const save = async (payload: CreateCreatorSegmentDto) => {
    try {
      isLoading.value = true
      error.value = null
      return await socialMediaClient.creatorSegments.create(payload)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { save, isLoading, error }
}

export const useUpdateSegment = (
  id: Ref<string>,
): UseSaveSegmentReturn<UpdateCreatorSegmentDto> => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const save = async (payload: UpdateCreatorSegmentDto) => {
    try {
      isLoading.value = true
      error.value = null
      return await socialMediaClient.creatorSegments.update(id.value, payload)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { save, isLoading, error }
}

const { preview } = socialMediaClient.creatorSegments

type SegmentPreviewResponse = Parameters<typeof adaptSegmentPreview>[0]

const handleSegmentPreviewPayload = (filter: SegmentPreviewFilter): PreviewCreatorSegmentDto =>
  filter.segmentId
    ? {
        segment_id: filter.segmentId,
        manual_members: filter.manualMembers,
        excluded_members: filter.excludedMembers,
      }
    : {
        advertiser_group_id: filter.advertiserGroupId,
        criteria: filter.criteria,
        manual_members: filter.manualMembers,
        excluded_members: filter.excludedMembers,
      }

export const useGetSegmentPreview = (
  filterRef: Ref<SegmentPreviewFilter>,
): UseGetSegmentPreviewReturn => {
  const {
    data: response,
    isLoading,
    error,
    fetchData: refetch,
  } = useApi(async (signal) => {
    const filter = filterRef.value
    const data = await preview(
      handleSegmentPreviewPayload(filter),
      handleSegmentPreviewParams(filter),
      { signal },
    )

    // The matched values are keyed by the criteria that produced them, so the
    // two are kept together rather than read from a filter that may have moved on.
    return { data, criteria: filter.criteria }
  })

  const result = computed(() =>
    response.value
      ? adaptSegmentPreview(
          response.value.data as SegmentPreviewResponse,
          response.value.criteria as SegmentPreviewFilter['criteria'],
        )
      : null,
  )

  const data = computed(() => result.value?.creators ?? [])
  const totalCount = computed(() => result.value?.totalCount ?? 0)
  const totalMembers = computed(() => result.value?.totalMembers ?? 0)
  const totalPages = computed(() => result.value?.totalPages ?? 0)
  const exceedsMemberCap = computed(() => result.value?.exceedsMemberCap ?? false)

  return { data, totalCount, totalMembers, totalPages, exceedsMemberCap, isLoading, error, refetch }
}

export const useDeleteSegment = (): {
  remove: (id: string) => Promise<boolean>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
} => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const remove = async (id: string) => {
    try {
      isLoading.value = true
      error.value = null
      await socialMediaClient.creatorSegments.remove(id)
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { remove, isLoading, error }
}
